import React, { useContext, useState, useEffect } from 'react';
import { AuthorContext, PageContext, routes } from 'gatsby-theme-apollo';
import Section from '../layout/section';
import PostPreviewCards from '../cards/post-preview-card';
import { Flex } from '../layout/flex';
import CTA from '../cta';
import { getAuthorPosts } from '../../services/post-service';
import { getAuthorTierLists } from '../../services/tier-list-service';
import StatusMessage from '../status/status-message';
import TierListPreviewCards from '../cards/tier-list-preview-card';
import { generateTierListPreviews } from '../../util/strip-data';

const MyPosts = () => {
  const { author: signedInAuthor } = useContext(AuthorContext);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [authorPosts, setAuthorPosts] = useState(false);
  const { gameName } = useContext(PageContext);

  const refreshAuthorPosts = async () => {
    const retrievedAuthorPosts = await getAuthorPosts({
      authorID: signedInAuthor && signedInAuthor.id
    });
    if (retrievedAuthorPosts) {
      let postsToRender = retrievedAuthorPosts;
      if (gameName) {
        postsToRender = retrievedAuthorPosts.filter(post =>
          post.games.some(game => game.name === gameName)
        );
      }
      setAuthorPosts(postsToRender);
    } else {
      setLoadError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (signedInAuthor) {
      if (!authorPosts) {
        setLoading(true);
        refreshAuthorPosts();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedInAuthor]);

  return (
    <div>
      {signedInAuthor && (
        <Section
          title={`Your Published Guides`}
          fullWidth
          linkText={'See All'}
          linkTarget={
            authorPosts &&
            authorPosts.length > 4 &&
            signedInAuthor &&
            routes.User(signedInAuthor.name)
          }
        >
          {loading || loadError || (authorPosts && authorPosts.length === 0) ? (
            <StatusMessage>
              {authorPosts &&
                authorPosts.length === 0 &&
                `You haven't made one for ${gameName} yet. Publish your first!`}
              {loading && '...'}
              {loadError && (
                <span style={{ textAlign: `center` }}>
                  Something went wrong loading your posts.
                  <br /> You should let @haxoleilas know in our{' '}
                  <a href="https://discord.gg/WWgcW8Z" target="_blank" rel="noreferrer">
                    Discord
                  </a>{' '}
                  so he can fix it faster.
                </span>
              )}
            </StatusMessage>
          ) : (
            authorPosts && <PostPreviewCards postPreviews={authorPosts} limit={4} disableShowMore />
          )}
        </Section>

      )}
      <Flex justifyContent="center">  
        <CTA campaignKey="MakeGuide" /> 
      </Flex>
    </div>
  );
};

const MyTierLists = () => {
  const { author: signedInAuthor } = useContext(AuthorContext);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [authorTierListPreviews, setAuthorTierListPreviews] = useState(false);
  const { gameName } = useContext(PageContext);

  const refreshAuthorTierLists = async () => {
    const retrievedAuthorTierLists = await getAuthorTierLists({
      authorID: signedInAuthor && signedInAuthor.id
    });
    if (retrievedAuthorTierLists) {
      let tierListsToRender = retrievedAuthorTierLists;
      if (gameName) {
        tierListsToRender = tierListsToRender.filter(post => post.game.name === gameName);
      }

      const tierListPreviews = generateTierListPreviews(tierListsToRender);
      setAuthorTierListPreviews(tierListPreviews);
    } else {
      setLoadError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (signedInAuthor) {
      if (!authorTierListPreviews) {
        setLoading(true);
        refreshAuthorTierLists();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedInAuthor]);

  return (
    <div>
      <Flex justifyContent="center">  
        <CTA campaignKey="MakeTierList" /> 
      </Flex>
      
      {signedInAuthor && (
        <Section
          title={`Your Published Tier Lists`}
          fullWidth
          linkText={'See All'}
          linkTarget={
            authorTierListPreviews &&
            authorTierListPreviews.length > 4 &&
            signedInAuthor &&
            routes.User(signedInAuthor.name)
          }
        >
          {loading ||
          loadError ||
          (authorTierListPreviews && authorTierListPreviews.length === 0) ? (
            <StatusMessage>
              {authorTierListPreviews &&
                authorTierListPreviews.length === 0 &&
                `You haven't made a tier list for ${gameName} yet. Publish your first!`}
              {loading && '...'}
              {loadError && (
                <span style={{ textAlign: `center` }}>
                  Something went wrong loading your Tier Lists.
                  <br /> You should let the team know in our{' '}
                  <a href="https://discord.gg/WWgcW8Z" target="_blank" rel="noreferrer">
                    Discord
                  </a>{' '}
                  so we can fix it faster.
                </span>
              )}
            </StatusMessage>
          ) : (
            authorTierListPreviews && (
              <TierListPreviewCards
                tierListPreviews={authorTierListPreviews}
                limit={4}
                disableShowMore
              />
            )
          )}
        </Section>
      )}
    </div>
  );
};

export { MyPosts, MyTierLists };
